
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';
import { DateTime } from 'luxon';
import { ValidationObserver } from 'vee-validate';

import MonthsField from '../../../fields/MonthsField.vue';
import SelectField from '../../../../framework/fields/SelectField.vue';
import SelectComponent from '../../../../framework/fields/SelectComponent.vue';
import MonthField from '../../../../framework/fields/MonthField.vue';
import DateField from '../../../../framework/fields/DateField.vue';

import { sharedState } from '../../../../framework/state';
import {
    getDatabaseOptions,
    getEnumerationOptions,
    getOptionLabel,
    OptionValue,
} from '../../../../framework/service/options';
import { dateToDbDateString } from '../../../../../common/framework/util/convert';
import { formatMonthStrings, monthDiff } from '../../../../../common/framework/util/time';

import { ExtendedVehicleCostMonthlyRow } from '../../../../../common/application/model/VehicleCostMonthlyRow';
import { InvoiceRowType } from '../../../../../common/application/model/enums/InvoiceRowType';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import { AddBalanceInvoiceReq } from '../../../../../common/application/model/api/AddBalanceInvoiceReq';
import { PageSize } from '../../../../../common/application/model/enums/PageSize';
import { Job } from '../../../../../common/framework/model/Job';
import { FrameworkResource } from '../../../../../common/framework/enumeration/FrameworkResource';
import { FinancingContractType } from '../../../../../common/application/model/enums/FinancingContractType';
import { VehicleState } from '../../../../../common/application/model/enums/VehicleState';

import AutoCompleteMultipleField from '../../../fields/AutoCompleteMultipleField.vue';
import InvoiceRowTypeSelectField from '../../../fields/InvoiceRowTypeSelectField.vue';
import ParameterService from '../../../service/parameter_service';

import { postBalanceInvoiceRequest } from '../../../client/sales_invoice_client';
import { exportPeriodBalance, getVehicleCostMonthlyRows } from '../../../client/vehicle_cost_monthly_row_client';
import { getCustomerOptions } from '../../../service/application_options_service';
import { formatCurrency } from '../../../service/helper_service';
import { errorToast, successToast } from '../../../service/toast_service';
import { getVehicleContracts } from '../../../service/resource_service';
import { getResourcesNew } from '../../../../framework/client/resource';
import { getBudgetExportFile } from '../../../client/file_meta_client';

@Component({
    components: {
        MonthField,
        MonthsField,
        SelectComponent,
        SelectField,
        AutoCompleteMultipleField,
        Multiselect,
        InvoiceRowTypeSelectField,
        ValidationObserver,
        DateField,
    },
})
export default class Budget extends Vue {
    readonly resourceType = ApplicationResource.VEHICLE_COST_MONTHLY_ROW;
    parameterService = new ParameterService(this.resourceType);

    @Prop(String) readonly customerId!: string;

    shared = sharedState;
    local = {
        rows: [] as ExtendedVehicleCostMonthlyRow[],
        loading: false,
        selected: undefined as ExtendedVehicleCostMonthlyRow | undefined,
        customerOptions: [] as OptionValue[],
        invoiceRowTypeOptions: [] as OptionValue[],
        vehicleContractOptions: [] as OptionValue[],
        pageSizeOptions: [] as number[],
        firstDayOfBeginMonth: undefined as string | undefined | null,
        firstDayOfEndMonth: undefined as string | undefined | null,
        entryDate: undefined as string | undefined | null,
        parameters: this.parameterService.get(),
        selectedInvoiceRowTypes: [] as OptionValue[],
        total: 0,
        monthlyCostJobIsRunning: false,
        financingContractTypeOptions: [] as OptionValue[],
    };
    isComponentModalActive = false;

    async mounted() {
        if (this.$route.params && this.$route.params.customerId) {
            this.local.parameters.customerId = this.$route.params.customerId.split(',');
            this.parameterService.set(this.local.parameters);
        }

        this.local.invoiceRowTypeOptions = (await getDatabaseOptions('InvoiceRowType', this)).filter(
            ({ id }) => id !== undefined,
        );

        if (!this.local.firstDayOfBeginMonth) {
            const now = new Date();
            this.local.firstDayOfBeginMonth = dateToDbDateString(new Date(now.getFullYear(), 0, 1));
        }

        if (!this.local.firstDayOfEndMonth) {
            const now = DateTime.fromJSDate(this.endDate, { zone: 'UTC' }).toJSDate();
            this.local.firstDayOfEndMonth = dateToDbDateString(new Date(now.getFullYear(), now.getMonth(), 1));
        }

        if (!this.local.entryDate) {
            const now = new Date();
            this.local.entryDate = dateToDbDateString(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
        }

        this.local.pageSizeOptions = Object.values(PageSize).filter((o) => typeof (o as any) === 'number') as number[];

        this.local.financingContractTypeOptions = getEnumerationOptions(
            'FinancingContractType',
            Object.keys(FinancingContractType),
            this,
        );

        this.setCustomParameters();
        await this.loadAsyncData();

        if (this.local.selectedInvoiceRowTypes.length === 0) {
            this.local.selectedInvoiceRowTypes = [...this.local.invoiceRowTypeOptions];
        }
    }

    @Watch('local.firstDayOfBeginMonth')
    @Watch('local.firstDayOfEndMonth')
    async beginMonthChange() {
        if (this.local.loading) {
            return;
        }

        await this.setMonthIn();
    }

    @Watch('local.parameters.pageSize')
    async pageSizeChange() {
        if (this.local.loading) {
            return;
        }

        await this.resetPagingAndLoadData();
    }

    async addBalanceInvoice() {
        if (!this.isAdmin) {
            return;
        }

        const selectedInvoiceRowTypes =
            this.local.selectedInvoiceRowTypes.length > 0 ? this.local.selectedInvoiceRowTypes.map(({ id }) => id) : [];

        for (const customerId of this.local.parameters.customerId) {
            const customerHasRows = this.local.rows.some((costRow) => costRow.customerId === customerId);

            if (!customerHasRows) {
                continue;
            }

            const vehicleContractId =
                this.local.parameters.vehicleContractId && this.local.parameters.vehicleContractId.length === 1
                    ? this.local.parameters.vehicleContractId[0]
                    : undefined;

            const addBalanceInvoiceReq: AddBalanceInvoiceReq = {
                customerId,
                vehicleContractId: vehicleContractId,
                balancePeriodBeginDate: new Date(this.local.firstDayOfBeginMonth!!),
                balancePeriodEndDate: new Date(this.local.firstDayOfEndMonth!!),
                invoiceEntryDate: new Date(this.local.entryDate!!),
                invoiceRowTypes: selectedInvoiceRowTypes as InvoiceRowType[],
            };

            try {
                await postBalanceInvoiceRequest(addBalanceInvoiceReq);
                successToast(this, 'message.balanceInvoiceAdded');
            } catch (e) {
                errorToast(this);
            }
        }
    }

    getCustomerName(customerIds: string[]): string {
        if (!customerIds.length) {
            return '';
        }

        return getOptionLabel(customerIds[0], this.local.customerOptions);
    }

    getInvoiceRowTypeLabel(id: string): string {
        return getOptionLabel(id, this.local.invoiceRowTypeOptions);
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }

    dropdownClosed(resourceIds: string[], idField: string): void {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        this.resetPagingAndLoadData();
    }

    onRowClick(row: ExtendedVehicleCostMonthlyRow): void {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    vehicleRegistrationNumberParameters(): Map<string, any> {
        const parameters = new Map<string, any>();

        if (this.local.parameters.customerId) {
            parameters.set('customerId', this.local.parameters.customerId);
        }

        return parameters;
    }

    vehicleVehicleStateParameters(): Map<string, any> {
        const parameters = new Map<string, any>();

        if (this.local.parameters.customerId) {
            parameters.set('customerId', this.local.parameters.customerId);
        }

        return parameters;
    }

    async vehicleStatesSelected(resourceIds: VehicleState[], idField: 'vehicleState'): Promise<void> {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        await this.resetPagingAndLoadData();
    }

    get maxDate(): Date {
        if (!this.isAdmin) {
            return DateTime.utc().minus({ days: 45 }).toJSDate();
        } else {
            return DateTime.utc().plus({ year: 1 }).endOf('year').startOf('month').toJSDate();
        }
    }

    get endDate(): Date {
        if (!this.isAdmin) {
            return DateTime.utc().minus({ days: 45 }).toJSDate();
        } else {
            return DateTime.utc().endOf('year').startOf('month').toJSDate();
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get sumInvoiced(): number {
        return this.local.rows.reduce((previousValue, currentRow) => previousValue + (currentRow.invoiced || 0), 0);
    }

    get sumPaid(): number {
        return this.local.rows.reduce((previousValue, currentRow) => previousValue + (currentRow.paid || 0), 0);
    }

    get sumBalance(): number {
        return this.local.rows.reduce(
            (previousValue, currentRow) => previousValue + ((currentRow.invoiced || 0) - (currentRow.paid || 0)),
            0,
        );
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        await this.resetPagingAndLoadData();
    }

    async exportToExcel(): Promise<void> {
        if (
            !this.local.parameters.customerId.length ||
            !this.local.firstDayOfBeginMonth ||
            !this.local.firstDayOfEndMonth
        ) {
            return;
        }

        const response = await exportPeriodBalance(this.parameterService.getBackendParameters());

        const result = await getBudgetExportFile(response.fileName);
        const aElement = document.createElement('a');
        aElement.setAttribute('download', response.fileName);
        const href = URL.createObjectURL(result);
        aElement.href = href;
        aElement.setAttribute('target', '_blank');
        aElement.click();
        URL.revokeObjectURL(href);
    }

    private async loadAsyncData() {
        this.local.loading = true;
        this.local.total = 0;

        this.local.customerOptions = await getCustomerOptions();
        this.local.rows = [];

        if (!this.local.firstDayOfBeginMonth || !this.local.firstDayOfEndMonth) {
            this.local.loading = false;
            return;
        }

        if (this.local.firstDayOfBeginMonth > this.local.firstDayOfEndMonth) {
            this.local.loading = false;
            return;
        }

        if (!this.local.parameters.customerId) {
            this.local.loading = false;
            return;
        }

        const customerIds = this.local.parameters.customerId || [];
        this.local.vehicleContractOptions = [{ id: undefined as string | undefined, label: '' }].concat(
            (await getVehicleContracts(customerIds)).map((o) => {
                return { id: o.id, label: o.vehicleRegistrationNumber ?? '-' };
            }),
        );

        const backendParameters = this.parameterService.getBackendParameters();

        const specialParameters = new Map<string, any>(); //specialParameters are used to create WHERE clause which is not belong to vehicle_cost_monthly_row table

        if (backendParameters.has('vehicleState')) {
            specialParameters.set('vehicleState', backendParameters.get('vehicleState'));
            backendParameters.delete('vehicleState');
        }

        if (backendParameters.has('financingContractType')) {
            specialParameters.set('financingContractType', backendParameters.get('financingContractType'));
            backendParameters.delete('financingContractType');
        }

        const monthlyRowResults = await getVehicleCostMonthlyRows(backendParameters, specialParameters);

        this.local.total = monthlyRowResults.total;
        this.local.rows = monthlyRowResults.vehicleCostMonthlyRows.sort((rowA, rowB) =>
            rowA.vehicleRegistrationNumber.localeCompare(rowB.vehicleRegistrationNumber),
        );

        await this.checkIfJobIsRunning();

        this.local.loading = false;
    }

    private setCustomParameters(): void {
        if (this.customerId) {
            this.local.parameters.customerId = [this.customerId];
        }

        if (this.local.parameters.month) {
            const months = this.local.parameters.month;
            this.local.firstDayOfBeginMonth = months[0];
            this.local.firstDayOfEndMonth = months[months.length - 1];
        } else {
            this.setMonthIn();
        }

        if (this.local.parameters.invoiceRowType) {
            const selectedInvoiceRowTypes = this.local.parameters.invoiceRowType;

            for (const invoiceRowType of selectedInvoiceRowTypes) {
                const option = this.local.invoiceRowTypeOptions.find(({ id }) => id === invoiceRowType);

                if (!option) {
                    continue;
                }

                this.local.selectedInvoiceRowTypes.push(option);
            }
        }
    }

    private async setMonthIn(): Promise<void> {
        const beginDate = new Date(this.local.firstDayOfBeginMonth!!);
        const endDate = new Date(this.local.firstDayOfEndMonth!!);
        const monthCount = monthDiff(beginDate, endDate);
        this.local.parameters.month = formatMonthStrings(beginDate.getFullYear(), beginDate.getMonth(), monthCount);

        await this.resetPagingAndLoadData();
    }

    @Watch('local.parameters.financingContractType')
    selectedFinancingContractTypeChange() {
        if (this.local.loading) {
            return;
        }

        this.resetPagingAndLoadData();
    }

    private async checkIfJobIsRunning() {
        if (!this.local.parameters.customerId) {
            return;
        }
        const jobParameters = new Map<string, any>([
            ['dataId', this.local.parameters.customerId],
            ['key', 'customer-update-vehicle-monthly-cost-reports'],
        ]);
        const runningJobs = await getResourcesNew<Job>(`${FrameworkResource.JOB}/status`, -1, jobParameters);

        this.local.monthlyCostJobIsRunning = runningJobs.length > 0;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
